import {
  collection,
  doc,
  FieldPath,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { z } from "zod";

import { db } from "integrations/firebase/firestore";

// The session, not useAuthUser: integrations/firebase/auth runs getAuth() at
// module load, which every component test rendering a person would pay.
import { useSession } from "../auth/session";
import { attendeeRecordSchema } from "../admin/use-attendee-records";
import { connectionSchema, eventsNewestFirst } from "../connections/schemas";
import { publicEventSchema } from "../events/schemas";
import {
  directoryAttendeeSchema,
  signalSchema,
  signalSelectionSchema,
  speakerSchema,
} from "./schemas";
import {
  resolveSignals,
  sortSignals,
  type ResolvedSignal,
} from "./signal-utils";

/**
 * Who to show. An attendee is their roster id; a speaker is their speaker
 * document, which may link a roster id of its own.
 */
export type EventPersonTarget = { attendeeId: string } | { speakerId: string };

export interface EventPersonRequest {
  eventId: string;
  target: EventPersonTarget;
  /**
   * The organiser's view (the Requests tab): the attendee record itself,
   * email and LinkedIn as the organiser sees them, and neither the viewer's
   * private note nor Connect.
   */
  variant?: "member" | "organiser";
  /**
   * On the connections page: the other person's uid. Their card as they
   * shared it when you met is what the sheet shows, which outlives the event.
   */
  connectionUid?: string;
}

export interface EventPersonProfile {
  name: string;
  role?: string;
  company?: string;
  avatar?: string;
  bio?: string;
}

export interface EventPerson {
  profile: EventPersonProfile;
  /** The roster id behind the person, when there is one. */
  attendeeId?: string;
  /** Only what the person shares and the event lets the directory carry. */
  email?: string;
  linkedinUrl?: string;
  signals: ResolvedSignal[];
  /**
   * Where the viewer's private note lives, or null for no note: the organiser
   * view, a speaker with no roster row, and the viewer's own profile.
   */
  notes: null | {
    attendeeId: string;
    /** For the connections page, when the event is gone. */
    fallbackEvent?: { title: string; slug: string };
  };
  /** Connect, while the event has connections switched on; else null. */
  connect: null | {
    connected: boolean;
    event: { eventId: string; slug: string; title: string };
  };
}

export type EventPersonState =
  | { status: "loading" }
  | { status: "missing" }
  | { status: "ready"; person: EventPerson };

const viewerMembershipSchema = z.object({
  attendeeId: z.string().optional(),
  eventTitle: z.string().optional(),
  eventSlug: z.string().nullable().optional(),
});

interface ViewerContext {
  /** The viewer's own roster row, to leave the note off their own profile. */
  ownAttendeeId?: string;
  /** False while the event's public projection could not be read. */
  known: boolean;
  shareContact: boolean;
  connections: boolean;
  event?: { eventId: string; slug: string; title: string };
}

const UNKNOWN_EVENT: ViewerContext = {
  known: false,
  shareContact: false,
  connections: false,
};

/**
 * The viewer's side of the event: their membership, and the event's
 * Attendees and Connect switches from its public projection. Read once per
 * viewer and event for five seconds, so opening one person after another costs
 * nothing extra. A longer window delays a settings change on an open page by
 * that long on top of the projection's own lag.
 */
const contextCache = new Map<
  string,
  { at: number; value: Promise<ViewerContext> }
>();
const CONTEXT_TTL_MS = 5_000;

export function clearEventPersonCache(): void {
  contextCache.clear();
}

function viewerContext(uid: string, eventId: string): Promise<ViewerContext> {
  const key = `${uid}/${eventId}`;
  const cached = contextCache.get(key);
  if (cached && Date.now() - cached.at < CONTEXT_TTL_MS) return cached.value;
  const value = readViewerContext(uid, eventId);
  contextCache.set(key, { at: Date.now(), value });
  return value;
}

async function readViewerContext(
  uid: string,
  eventId: string,
): Promise<ViewerContext> {
  const membership = await getDoc(doc(db, "events", eventId, "members", uid))
    .then((snapshot) =>
      snapshot.exists()
        ? viewerMembershipSchema.safeParse(snapshot.data())
        : null,
    )
    .catch(() => null);
  if (!membership?.success) return UNKNOWN_EVENT;
  const ownAttendeeId = membership.data.attendeeId;
  const slug = membership.data.eventSlug;
  if (!slug) return { ...UNKNOWN_EVENT, ownAttendeeId };
  const projection = await getDoc(doc(db, "public_events", slug))
    .then((snapshot) =>
      snapshot.exists() ? publicEventSchema.safeParse(snapshot.data()) : null,
    )
    .catch(() => null);
  if (!projection?.success) return { ...UNKNOWN_EVENT, ownAttendeeId };
  const event = projection.data;
  return {
    ownAttendeeId,
    known: true,
    // Absent means on (D5), the same default as the Attendees tab.
    shareContact: event.configAttendees !== false,
    // Absent means off (TNG-93).
    connections: event.configConnections === true,
    event: { eventId: event.eventId, slug: event.slug, title: event.title },
  };
}

async function readDoc<T>(
  path: [string, ...string[]],
  schema: z.ZodType<T>,
): Promise<T | null> {
  try {
    const snapshot = await getDoc(doc(db, ...path));
    if (!snapshot.exists()) return null;
    const parsed = schema.safeParse({ id: snapshot.id, ...snapshot.data() });
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

async function readSignals(
  eventId: string,
  attendeeId: string | undefined,
): Promise<ResolvedSignal[]> {
  if (!attendeeId) return [];
  try {
    const [selection, signals] = await Promise.all([
      readDoc(
        ["events", eventId, "signal_selections", attendeeId],
        signalSelectionSchema,
      ),
      getDocs(collection(db, "events", eventId, "signals")),
    ]);
    if (!selection) return [];
    const live = signals.docs.flatMap((item) => {
      const parsed = signalSchema.safeParse({ id: item.id, ...item.data() });
      return parsed.success ? [parsed.data] : [];
    });
    return resolveSignals(sortSignals(live), selection.signalIds);
  } catch {
    // A missing badge is the correct degraded rendering.
    return [];
  }
}

async function isConnected(
  uid: string,
  eventId: string,
  attendeeId: string,
): Promise<boolean> {
  try {
    const found = await getDocs(
      query(
        collection(db, "users", uid, "connections"),
        where(new FieldPath("events", eventId, "attendeeId"), "==", attendeeId),
        limit(1),
      ),
    );
    return !found.empty;
  } catch {
    return false;
  }
}

async function loadOrganiserView(
  eventId: string,
  target: EventPersonTarget,
): Promise<EventPerson | null> {
  if (!("attendeeId" in target)) return null;
  const record = await readDoc(
    ["events", eventId, "attendees", target.attendeeId],
    attendeeRecordSchema,
  );
  if (!record) return null;
  const { name, role, company, avatar, bio } = record;
  return {
    profile: { name, role, company, avatar, bio },
    attendeeId: record.id,
    email: record.email,
    linkedinUrl: record.linkedinUrl,
    signals: await readSignals(eventId, record.id),
    notes: null,
    connect: null,
  };
}

async function loadMemberView(
  uid: string | null,
  { eventId, target, connectionUid }: EventPersonRequest,
): Promise<EventPerson | null> {
  const context = uid ? await viewerContext(uid, eventId) : UNKNOWN_EVENT;

  const speaker =
    "speakerId" in target
      ? await readDoc(
          ["events", eventId, "speakers", target.speakerId],
          speakerSchema,
        )
      : null;
  const attendeeId =
    "attendeeId" in target ? target.attendeeId : speaker?.attendeeId;
  const [row, connection, signals] = await Promise.all([
    attendeeId
      ? readDoc(
          ["events", eventId, "directory", attendeeId],
          directoryAttendeeSchema,
        )
      : Promise.resolve(null),
    uid && connectionUid
      ? readDoc(["users", uid, "connections", connectionUid], connectionSchema)
      : Promise.resolve(null),
    readSignals(eventId, attendeeId),
  ]);

  let profile: EventPersonProfile;
  let email: string | undefined;
  let linkedinUrl: string | undefined;
  if (connection) {
    // What they shared when you met; the backend already applied the event's
    // contact switch and their own sharing choices at that moment.
    const { name, role, company, avatar } = connection;
    profile = { name, role, company, avatar, bio: row?.bio };
    email = connection.email;
    linkedinUrl = connection.linkedinUrl;
  } else if (speaker) {
    const { name, role, company, avatar, bio } = speaker;
    profile = { name, role, company, avatar, bio };
  } else if (row) {
    const { name, role, company, avatar, bio } = row;
    profile = { name, role, company, avatar, bio };
  } else {
    return null;
  }
  if (!connection && row && context.shareContact) {
    email = row.email;
    linkedinUrl = row.linkedinUrl;
  }

  const latest = connection ? eventsNewestFirst(connection)[0] : undefined;
  const isSelf =
    attendeeId !== undefined && attendeeId === context.ownAttendeeId;
  const notes =
    attendeeId && !isSelf
      ? {
          attendeeId,
          ...(latest && latest.eventId === eventId
            ? { fallbackEvent: { title: latest.title, slug: latest.slug } }
            : {}),
        }
      : null;

  const connect =
    uid && attendeeId && !isSelf && !connectionUid && context.connections
      ? context.event
        ? {
            connected: await isConnected(uid, eventId, attendeeId),
            event: context.event,
          }
        : null
      : null;

  return { profile, attendeeId, email, linkedinUrl, signals, notes, connect };
}

/**
 * Everything the person sheet shows, loaded from the ids alone. The one place
 * that decides what a viewer may see about a person: callers pass ids and
 * never a profile, so no surface can show more, or less, than another.
 *
 * - Profile: the directory row, the speaker document for a speaker, or the
 *   connection card on the connections page.
 * - Email and LinkedIn: only while the event's Attendees switch is on
 *   (`configAttendees !== false`), and only what the directory row carries,
 *   which is what the person chose to share.
 * - Signals: the person's picks, when they have a roster row.
 * - Private note: for anybody with a roster row except the viewer.
 * - Connect: while the event has connections on, for somebody else.
 */
export function useEventPerson(
  request: EventPersonRequest | null,
): EventPersonState {
  const uid = useSession()?.uid ?? null;
  // Tagged with the request it answers, so reopening the sheet on somebody
  // else never shows the previous person for a render.
  const [answer, setAnswer] = useState<{
    key: string;
    state: EventPersonState;
  } | null>(null);
  // Primitives, because callers build a new request object on every render.
  const eventId = request?.eventId;
  const attendeeId =
    request && "attendeeId" in request.target
      ? request.target.attendeeId
      : undefined;
  const speakerId =
    request && "speakerId" in request.target
      ? request.target.speakerId
      : undefined;
  const variant = request?.variant ?? "member";
  const connectionUid = request?.connectionUid;
  const key = [
    eventId,
    attendeeId,
    speakerId,
    variant,
    connectionUid,
    uid,
  ].join("|");

  useEffect(() => {
    if (!eventId || (!attendeeId && !speakerId)) return;
    const target: EventPersonTarget = attendeeId
      ? { attendeeId }
      : { speakerId: speakerId as string };
    let cancelled = false;
    const load =
      variant === "organiser"
        ? loadOrganiserView(eventId, target)
        : loadMemberView(uid, { eventId, target, connectionUid });
    void load.then((person) => {
      if (cancelled) return;
      setAnswer({
        key,
        state: person ? { status: "ready", person } : { status: "missing" },
      });
    });
    return () => {
      cancelled = true;
    };
  }, [key, eventId, attendeeId, speakerId, variant, connectionUid, uid]);

  return request && answer?.key === key ? answer.state : { status: "loading" };
}
